import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { parseTicketTokens } from './ticketImport.js';

/** TicketImportModal の props。 */
export interface TicketImportModalProps {
  /** 開いているか。 */
  open: boolean;
  /** モーダルを閉じる。 */
  onClose: () => void;
  /** 取込実行中か（照会 API 待ち）。 */
  importing: boolean;
  /** 直近の取込で引けなかった案件番号。空配列なら表示しない。 */
  notFound: string[];
  /** 直近の取込で失敗した理由（API エラー）。null なら表示しない。 */
  errorMessage: string | null;
  /** 取込を実行する（案件番号のトークン列を渡す）。 */
  onImport: (tokens: string[]) => void;
}

/**
 * 覆いは `body` の直下へ `createPortal` で出し、層は `--z-modal` で決める。共有シェル
 * (`AppShell`) の本文ラッパーは登場アニメーション後も `transform: translateY(0)` を残し、
 * それが `position: fixed` の包含ブロックになる。木の中に置いたままだと覆いは本文の枠に
 * 閉じ込められ、サイドバー（`--z-sticky`=100）の領域まで届かない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox のチケット一括取込モーダル。Google スプレッドシートの案件番号列をそのまま貼り付けられる複数行テキストエリアを出し、解釈できた件数を提示して取込を実行する。引けなかった案件番号はモーダル内に残して提示する
 * @graph-connects parseTicketTokens [calls] 貼り付けテキストを案件番号トークン列へ分割
 */
export function TicketImportModal({
  open,
  onClose,
  importing,
  notFound,
  errorMessage,
  onImport,
}: TicketImportModalProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  // 開くたびに入力を空へ戻す（前回の貼り付けが残っていると二重取込しやすい）。
  useEffect(() => {
    if (open) setText('');
  }, [open]);

  // Esc で閉じる。取込中は閉じない（結果を取りこぼさないため）。
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !importing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, importing, onClose]);

  const tokens = useMemo(() => parseTicketTokens(text), [text]);
  const canImport = tokens.length > 0 && !importing;

  const handleImport = useCallback(() => {
    if (tokens.length === 0 || importing) return;
    onImport(tokens);
  }, [tokens, importing, onImport]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={() => {
        if (!importing) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('pjSandbox.import.title')}
        className="flex w-full max-w-lg flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5 shadow-xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2 className="mb-1 text-lg font-semibold text-[var(--color-text-primary)]">
          {t('pjSandbox.import.title')}
        </h2>
        <p className="mb-4 text-xs text-[var(--color-text-secondary)]">
          {t('pjSandbox.import.hint')}
        </p>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-[var(--color-text-secondary)]">
            {t('pjSandbox.import.inputLabel')}
          </span>
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            rows={10}
            spellCheck={false}
            placeholder={t('pjSandbox.import.placeholder')}
            aria-label={t('pjSandbox.import.inputLabel')}
            className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1.5 font-mono text-sm text-[var(--color-text-primary)]"
          />
        </label>

        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t('pjSandbox.import.parsedCount', { count: tokens.length })}
        </p>

        {errorMessage !== null && (
          <p className="mt-2 text-xs text-[var(--color-error)]">{errorMessage}</p>
        )}

        {notFound.length > 0 && (
          <div className="mt-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] p-2">
            <p className="text-xs font-medium text-[var(--color-warning)]">
              {t('pjSandbox.import.notFound', { count: notFound.length })}
            </p>
            <p className="mt-1 break-all font-mono text-[11px] text-[var(--color-text-muted)]">
              {notFound.join(', ')}
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('pjSandbox.import.close')}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!canImport}
            className="rounded-md bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importing ? t('pjSandbox.import.importing') : t('pjSandbox.import.submit')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
